import * as THREE from "three";
import type { LevelSim } from "@highmarch/domain";
import { audio } from "../audio/audio";
import { attachBossMusic } from "../audio/bossMusic";
import { subscribeBossDusk } from "../render/bossDusk";
import { EffectsView } from "../render/effects";
import { FxHooks } from "../render/fxHooks";
import { GroundMesh } from "../render/groundMesh";
import { HqMaterials } from "../render/hqMaterials";
import { PostFx } from "../render/postfx";
import { tierBand } from "../render/quality";
import type { SceneRig } from "../render/scene";
import { Terrain } from "../render/terrain";
import { VfxRouter } from "../render/vfxRouter";
import { TowersV2Fx } from "../render/towersV2Fx";
import { EnemiesV2Fx } from "../render/enemiesV2Fx";
import { EnemyViews, GridView, SurroundView, PlacementView, RoadView, TowerViews, TroopViews } from "../render/views";
import { CritterViews } from "../render/views/critters";
import { PetView } from "../render/views/petView";
import { WildPetView } from "../render/views/wildPetView";
import { DetailScatter } from "../render/views/detail";
import { FoliageView } from "../render/views/foliage";
import { Meadow } from "../render/views/meadow";
import { ScreenFlash } from "../ui/screenFlash";
import { reducedVfx } from "../ui/settings";
import type { HitStop } from "./hitStop";
import { listen } from "./listen";
import { appHeight } from "../ui/appSize";
import { isRareEnemy } from "../rare/rareApi";

/**
 * The level's 3D world: builds every view over the sim and wires the VFX router and the boss hooks into them, then
 * keeps them in step (the per-tick hooks, the per-frame updates, the road rebuild) and frees them. No input, no HUD.
 */
export class LevelScene {
  readonly surround: SurroundView;
  readonly grid: GridView;
  readonly road = new RoadView();
  readonly enemies: EnemyViews;
  readonly towers: TowerViews;
  readonly troopViews: TroopViews;
  /** The pet by the rally flag (docs/PETS.md), in a run that took one. */
  readonly pet: PetView;
  /** Pets v2: the wild pup on the grass, and on the road once tapped (render/views/wildPetView.ts). */
  readonly wild: WildPetView;
  readonly critters: CritterViews;
  /** Exposed for the headless captures, which probe the generated island geometry. */
  readonly ground: GroundMesh;
  readonly detail: DetailScatter;
  readonly placement = new PlacementView();
  readonly effects: EffectsView;
  /** The Mortar's shells, the Arcane Spires' beams, the Hex Totems' wards and the Barracks' pennants (TOWERS_V2). */
  readonly towersV2: TowersV2Fx;
  /** The M21 enemies' telegraphs, dives, rune shields and shockwaves (docs/ENEMIES_V2.md). */
  readonly enemiesV2: EnemiesV2Fx;
  /** The sprite VFX and projectiles, once they have loaded (null until then and after dispose). */
  router: VfxRouter | null = null;
  /**
   * Towers between the camera and a live enemy fade (TowerOcclusion). The play view needs it to keep raiders tappable;
   * the title's low cinematic camera (render/titleDiorama.ts) would see half its towers ghosted, so it turns it off.
   */
  occlude = true;
  private roadDirty = false;
  private lastRoadTiles = -1;
  private lastPortalX = NaN;
  private lastPortalY = NaN;
  private disposed = false;

  /** `flyCoins` sends a boss's reward coins from a screen point (CSS px) into the points pill. */
  constructor(private readonly rig: SceneRig, private readonly sim: LevelSim, seed: bigint, level: number, hitStop: HitStop, flyCoins: (x: number, y: number, n: number) => void) {
    FoliageView.setAuthored(sim.level.terrain?.props ?? []);
    Terrain.current = new Terrain(sim.grid, Number(seed % 2147483647n) ^ (level * 7919), 1, level, sim.level.terrain);
    Terrain.current.setRoad(sim.road);
    this.surround = new SurroundView(sim.grid);
    this.grid = new GridView(sim.grid);
    this.enemies = new EnemyViews(sim);
    EnemyViews.lodFar = () => rig.iso.currentZoom >= EnemyViews.LodZoom;
    // M36: a rare is drawn as a boss but has its own look and music (level/rareMoment.ts): the boss hooks pass it by
    const isRare = (b: unknown): boolean => !!b && typeof b === "object" && "id" in b && isRareEnemy(sim, Number((b as { id: number }).id));
    subscribeBossDusk(this.enemies, rig.dusk, isRare);         // boss dusk / sun-break (render/bossDusk.ts); rig.frame resets it per map
    // Boss moments (units P2): trauma, hit-stop and the boss's reward coins flying from the body into the points pill.
    this.enemies.beats.addTrauma = (t) => { if (!reducedVfx()) rig.iso.addTrauma(t); };
    this.enemies.beats.hitStop = hitStop;
    // Phase 2 meeting points (render/fxHooks.ts): the strike and the boss fall reach ui's DOM flash and look's exposure
    // lift by name, so neither the VFX module nor the boss stage imports the UI or the post chain.
    FxHooks.screenFlash = (c, p, ms) => ScreenFlash.flash(c, p, ms);
    FxHooks.exposurePunch = (g, ms) => PostFx.exposurePunch(g, ms);
    this.enemies.beats.screenFlash = (c, p, ms) => FxHooks.screenFlash?.(c, p, ms);
    this.enemies.beats.coinsTo = (w, n) => { const v = w.clone().project(rig.camera); flyCoins(((v.x + 1) / 2) * innerWidth, ((1 - v.y) / 2) * appHeight(), n); };
    this.enemies.onBossDeath.push((_b, at) => this.grid.foliage.gustAround({ x: at.x, y: at.z }, 3));   // the trees bow to the fall
    attachBossMusic(this.enemies, isRare as (b: never) => boolean);   // the boss bed and its phase beat (WP10, A3)
    this.towers = new TowerViews(sim);
    this.towers.enemyFeet = (id) => this.enemies.feet(id);           // weapons track the enemies where they are drawn
    this.effects = new EffectsView(sim);
    this.towersV2 = new TowersV2Fx(sim, () => rig.camera);
    this.towersV2.attach((id, part) => this.towers.nodeWorld(id, part), (id) => this.enemies.feet(id), (id) => this.enemies.drawnPoints(id)?.[0] ?? null, (id, hidden) => this.enemies.setBodyHidden(id, hidden));
    this.enemiesV2 = new EnemiesV2Fx(sim);
    this.enemiesV2.attach((id) => this.enemies.viewOf(id), (id) => this.enemies.feet(id), (id) => this.enemies.drawnPoints(id)?.[0] ?? null);
    this.enemiesV2.addTrauma = (t) => { if (!reducedVfx()) rig.iso.addTrauma(t); };
    // Placement and construction dust now come from the sprite VFX (vfx.towerPlace on TowerPlaced, landing with the
    // tower's 120 ms drop-in); the old EffectsView.buildDust point burst doubled it and is gone (vfx.md F11).
    VfxRouter.create(rig.scene, rig.camera, sim).then((r) => {
      if (!r) return;
      if (this.disposed) { r.dispose(rig.scene); return; }      // the level went while it loaded: nothing else will free it
      this.attachRouter(r, hitStop);
    });
    this.troopViews = new TroopViews(sim);
    // the pet sits where the camera sees it (never behind the fortress from where the player looks)
    const toCam = new THREE.Vector3();
    const toCamera = () => {
      const look = rig.iso.lookPoint();
      toCam.set(rig.camera.position.x - look.x, 0, rig.camera.position.z - look.z).normalize();
      return { x: toCam.x, z: toCam.z };
    };
    this.pet = new PetView(sim, toCamera);
    this.pet.abilities.troopAt = (id) => this.troopViews.drawnAt(id);
    this.pet.abilities.enemyAt = (id) => this.enemies.feet(id);
    this.wild = new WildPetView(sim, toCamera);
    this.wild.hideEnemy = (id, hidden, headY) => this.enemies.setBodyHidden(id, hidden, headY);
    this.wild.enemyAt = (id) => this.enemies.feet(id);
    this.critters = new CritterViews(sim);
    this.road.enemies = sim.enemySlots;                    // plank bridges sag under the units crossing them
    this.ground = new GroundMesh(sim.grid, Terrain.current);
    this.ground.build(sim.road);
    if (GroundMesh.currentLand) this.grid.foliage.dressBeyond(GroundMesh.currentLand, sim.grid, GroundMesh.FogFull);
    // props take the same map-edge haze the ground does, or they stand crisp above it
    HqMaterials.setMapHaze(-0.5, -0.5, sim.grid.width, sim.grid.height, rig.lookNow.sky, GroundMesh.FogStart, GroundMesh.FogFull);
    this.detail = new DetailScatter(sim.grid);
    this.detail.build();
    this.detail.setRoad(sim.road);
    this.roadDirty = true;
    rig.scene.add(...this.groups());
  }

  /** The loaded VFX router, hooked into the views and the camera. */
  private attachRouter(r: VfxRouter, hitStop: HitStop): void {
    const rig = this.rig, sim = this.sim;
    r.vfx.warm();
    this.effects.router = r;
    this.router = r;
    rig.scene.add(r.projectiles.group);
    r.towerNode = (id, node) => this.towers.nodeWorld(id, node);
    r.towerAimDelay = (id) => this.towers.aimDelay(id);
    r.towerAimDir = (id) => this.towers.muzzleDir(id);
    r.enemyNode = (id, node) => this.enemies.nodeWorld(id, node);
    r.enemyFeet = (id) => this.enemies.feet(id);
    r.enemyAir = (id) => this.enemies.airAim(id);
    this.enemies.fx = r.vfx;
    Meadow.attach(r.vfx);                               // leaves fall as ambient sprites; pollen yields to a busy pool
    r.addTrauma = (t) => { if (!reducedVfx()) rig.iso.addTrauma(t); };
    r.hitStop = hitStop;
    r.qualityTier = () => (rig.quality ? tierBand(rig.quality.current).index : 1);
    r.setGround(sim.grid.width, sim.grid.height);
    this.towers.fx = () => r.vfx;
    this.towersV2.fx = () => r.vfx;
    this.enemiesV2.fx = () => r.vfx;
    this.pet.fx = () => r.vfx;                           // the bark's shout and mote (render/views/petBark.ts)
    this.wild.fx = () => r.vfx;
    this.troopViews.fx = () => r.vfx;                    // a troop skin's blow trail and glint (render/skins/kit/troopFx.ts)
    this.pet.abilities.fx = () => r.vfx;
    this.towers.onBaseCharge = (tip, keep, seconds) => { r.vfx.baseCharge(tip, { keep, seconds }); audio.play("baseCharge"); };
    r.baseShotIsHero = (id) => this.towers.heroShot(id);
    this.effects.baseShotIsHero = (id) => this.towers.heroShot(id);
    // Boss arrival / fall VFX at the boss itself, from the units view's boss hooks (PHASE2_BRIEF "Boss events").
    listen(this.enemies, "onBossSpawn", (boss: { root: THREE.Object3D }) => r.bossArrived(boss.root));
    listen(this.enemies, "onBossDeath", (boss: { root: THREE.Object3D }, at?: THREE.Vector3) => r.bossFell(boss.root, at));
    r.bossViaView = true;
    r.onShake = (seconds, strength) => this.rig.shake(seconds, strength);
  }

  /** Every view's group, in the order they join the scene. */
  private groups(): THREE.Object3D[] {
    return [this.surround.group, this.grid.group, this.road.group, this.enemies.group, this.towers.group, this.troopViews.group, this.pet.group, this.wild.group, this.placement.group, this.effects.group, this.towersV2.group, this.enemiesV2.group, this.critters.group, this.ground.group, this.detail.group];
  }

  /** Marks the road views for a rebuild when the road grew or its portal moved since the last look (always, `force`). */
  noteRoad(force: boolean): void {
    const road = this.sim.road, portal = road.portal;
    if (force || road.designedTileCount !== this.lastRoadTiles || portal.x !== this.lastPortalX || portal.y !== this.lastPortalY) {
      this.roadDirty = true; this.lastRoadTiles = road.designedTileCount; this.lastPortalX = portal.x; this.lastPortalY = portal.y;
    }
  }

  /**
   * Rebuilds the road-dependent views — at most once a frame, however many commands landed since the last one.
   *
   * These are not cheap: the ground repaints its splat canvas and re-uploads the texture, the road view recreates
   * its tiles and the terrain recomputes its bridge spans. Doing that per COMMAND meant a player laying a card paid
   * for it immediately, and anything that issues commands in a loop — the headless level check, the capture's
   * drafting pass — paid for it dozens of times over with nothing rendered in between.
   */
  flush(): void {
    if (!this.roadDirty) return;
    this.roadDirty = false;
    const sim = this.sim;
    Terrain.current?.setRoad(sim.road);
    this.ground.build(sim.road);
    this.detail.setRoad(sim.road);                // hides the tufts the road now covers; never rebuilds the scatter
    this.road.rebuild(sim.road);
    this.grid.setRoad(sim.road.roadCells.filter((c) => !c.equals(sim.grid.base)));
    this.router?.setGround(sim.grid.width, sim.grid.height);     // the road bed changes the height the decals sit on
  }

  /** The unit views' per-tick hooks, once for every sim tick. */
  onTick(): void {
    this.enemies.onTick();
    this.towers.onTick();
    this.troopViews.onTick();
    this.pet.onTick();
    this.wild.onTick();
    this.towersV2.onTick();
    this.enemiesV2.onTick();
  }

  /** A resumed battle's first frame: the unit views catch up with the replayed sim (they build from its slots). */
  catchUp(): void {
    this.onTick();
    this.enemies.interpolate(1, 0); this.troopViews.interpolate(1, 0); this.pet.catchUp();
  }

  /** The per-frame view updates: the units at the tick fraction `alpha`, then everything that animates on its own. */
  update(dt: number, simDt: number, fxDt: number, alpha: number, now: number): void {
    this.enemies.interpolate(alpha, simDt);
    this.troopViews.interpolate(alpha, simDt);
    this.pet.interpolate(simDt);
    this.wild.interpolate(simDt);
    this.towers.update(simDt);
    this.towersV2.update(alpha, simDt);                    // after the towers posed: the beams leave the crystals as drawn
    this.enemiesV2.update(alpha, simDt);
    if (this.occlude) this.towers.fadeOccluders(dt, this.rig.camera);
    this.road.update(dt);
    this.grid.update(dt);
    this.critters.update(dt);
    this.ground.update(HqMaterials.time.value);
    // the road preview in the ground breathes on the placement highlights' beat
    GroundMesh.previewGlow.value = 0.55 + 0.45 * Math.sin(now / PlacementView.PulseMs);
    this.effects.update(fxDt);
  }

  /** The scene's major layers, for the perf overlay to hide one at a time and see what the frame rate is paying for. */
  layers(): Record<string, THREE.Object3D[]> {
    return {
      ground: [this.ground.group],
      scatter: [this.detail.group],
      props: [this.grid.group, this.surround.group, this.critters.group],
      towers: [this.towers.group],
      units: [this.enemies.group, this.troopViews.group, this.pet.group, this.wild.group],
      fx: [this.effects.group, this.towersV2.group, this.enemiesV2.group],
    };
  }

  /**
   * The end screen has settled and the rig rests (M33): the level draws no more, so its effects atlas (2048², with
   * mips ~21 MB on the GPU) is given back now rather than at teardown. Drawn again it would upload from its image.
   */
  rest(): void { this.router?.releaseGpu(); }

  dispose(): void {
    this.disposed = true;
    Terrain.current = null;
    this.effects.dispose(this.rig.scene);
    this.router = null;
    this.placement.clear();                                // the placement preview's rig and materials
    this.critters.dispose();
    this.ground.dispose();
    this.detail.dispose();
    this.grid.dispose();
    this.rig.scene.remove(...this.groups());
    this.enemies.dispose();                                // bone textures, per-view material clones, ring/bar instancing
    this.towers.dispose();
    this.towersV2.dispose();
    this.enemiesV2.dispose();
    this.troopViews.dispose();
    this.pet.dispose();
    this.wild.dispose();
    this.road.dispose();
    // The statics outlive the level: their closures held the level, so the last level's whole scene (and the end
    // screen's chest WebGL context) stayed reachable on the hub (RC1 FX-C).
    EnemyViews.lodFar = () => false;
    FxHooks.screenFlash = null;
    FxHooks.exposurePunch = null;
  }
}
